import { useState, useEffect, Fragment } from "react";
import { toast } from "sonner";
import {
  Database, Copy, BadgeCheck, KeyRound, ShieldCheck, Loader2, Download,
  AlertTriangle, ShieldAlert, GitCommitHorizontal, ArrowUp, ChevronDown, XCircle,
} from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { exportLedgerJson, csvExportUrl, tamperAudit } from "@/lib/api";
import { verifyEntry } from "@/lib/verify";

const VERIFY_CODE = `from cryptography.hazmat.primitives.asymmetric.ed25519 \\
    import Ed25519PublicKey

pub = Ed25519PublicKey.from_public_bytes(
    bytes.fromhex(export["governor_pubkey"]))

pub.verify(
    bytes.fromhex(export["signature"]),
    export["canonical"].encode())`;

const MerkleCell = ({ entry, broken, brokenFrom }) => {
  if (!entry.merkle_root)
    return <span className="mono text-[10px] text-slate-600">—</span>;
  const prev = entry.prev_root && entry.prev_root.length ? entry.prev_root : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="merkle-root-trigger"
          className={`group inline-flex items-center gap-1.5 mono text-[10px] hover:text-cyan-300 transition-colors ${
            broken === "tampered" ? "text-red-300" : broken === "downstream" ? "text-amber-300" : "text-violet-300"
          }`}
          title="View chain link"
        >
          {broken ? (
            <AlertTriangle className={`h-3 w-3 ${broken === "tampered" ? "text-red-400" : "text-amber-400"}`} />
          ) : (
            <GitCommitHorizontal className="h-3 w-3 opacity-70" />
          )}
          <span className="truncate max-w-[92px]">{entry.merkle_root.slice(0, 16)}…</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 bg-[#0b1120] border-slate-800 text-slate-200"
        data-testid="merkle-popover"
      >
        <p className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-3">
          Merkle Chain · batch #{String(entry.batch_seq).padStart(3, "0")}
        </p>
        <div className="space-y-3">
          <div>
            <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">This root</p>
            <p className="mono text-[10px] text-violet-300 break-all leading-relaxed">
              {entry.merkle_root}
            </p>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <span className="h-px flex-1 bg-slate-800" />
            <ArrowUp className="h-3 w-3" />
            <span className="mono text-[9px] uppercase tracking-widest">chains from</span>
            <span className="h-px flex-1 bg-slate-800" />
          </div>
          <div>
            <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Previous root</p>
            {prev ? (
              <p className="mono text-[10px] text-slate-400 break-all leading-relaxed">{prev}</p>
            ) : (
              <p className="mono text-[10px] text-emerald-300">GENESIS · batch #000 (chain origin)</p>
            )}
          </div>
          {broken && (
            <p className={`mono text-[9px] leading-relaxed border-t pt-2 ${
              broken === "tampered" ? "text-red-300 border-red-900/60" : "text-amber-300 border-amber-900/60"
            }`}>
              {broken === "tampered"
                ? "⚠ This record was altered after sealing — its signature no longer matches this root."
                : `⚠ Descends from altered entry #${String(brokenFrom).padStart(3, "0")}. This signature is still valid, but a link above it is broken, so chain integrity cannot be trusted.`}
            </p>
          )}
          <p className="mono text-[9px] text-slate-600 leading-relaxed border-t border-slate-800 pt-2">
            root = BLAKE2b( prev_root ‖ canonical ‖ governor_sig ‖ auditor_sig )
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const HashChip = ({ value, color = "text-slate-400", label = "Value" }) => {
  if (!value) return <span className="mono text-[10px] text-slate-600">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };
  return (
    <button
      onClick={copy}
      className={`group inline-flex items-center gap-1.5 mono text-[10px] ${color} hover:text-cyan-300 transition-colors`}
      title={value}
    >
      <span className="truncate max-w-[92px]">{value.slice(0, 16)}…</span>
      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
};

const Sig = ({ value }) => <HashChip value={value} label="Signature" />;

const VerifyButton = ({ result, expanded, onClick }) => {
  const valid = result?.valid;
  return (
    <button
      data-testid="verify-entry-button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 mono text-[10px] transition-colors ${
        valid === true
          ? "border-emerald-700/50 text-emerald-300 hover:border-emerald-500/70"
          : valid === false
          ? "border-red-700/50 text-red-300 hover:border-red-500/70"
          : "border-slate-700 text-slate-300 hover:border-cyan-600/60 hover:text-cyan-300"
      }`}
    >
      {valid === true ? <ShieldCheck className="h-3 w-3" /> : valid === false ? <XCircle className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
      {valid === true ? "Valid" : valid === false ? "Invalid" : "Verify"}
      <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
    </button>
  );
};

const SigLine = ({ label, ok }) => (
  <div className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-950/40 px-2.5 py-1.5">
    <span className="mono text-[10px] text-slate-400">{label}</span>
    {ok === null || ok === undefined ? (
      <span className="mono text-[10px] text-slate-500">n/a</span>
    ) : ok ? (
      <span className="mono text-[10px] text-emerald-300 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> valid</span>
    ) : (
      <span className="mono text-[10px] text-red-300 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> invalid</span>
    )}
  </div>
);

const VerifyDrawer = ({ entry, result }) => (
  <tr data-testid="verify-drawer" className="bg-slate-950/70 border-l-2 border-l-cyan-700/60">
    <td colSpan={11} className="px-4 py-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          {result?.valid ? (
            <span data-testid="signature-valid-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600/50 bg-emerald-950/50 px-3 py-1 mono text-[11px] font-semibold text-emerald-300">
              <ShieldCheck className="h-3.5 w-3.5" /> Signature Valid
            </span>
          ) : (
            <span data-testid="signature-invalid-badge" className="inline-flex items-center gap-1.5 rounded-full border border-red-600/50 bg-red-950/50 px-3 py-1 mono text-[11px] font-semibold text-red-300">
              <XCircle className="h-3.5 w-3.5" /> Signature Invalid
            </span>
          )}
          <span className="mono text-[10px] text-slate-500">verified in-browser · Ed25519 · no export required</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <SigLine label="Governor signature" ok={result?.governor} />
          <SigLine label="Auditor signature" ok={result?.auditor} />
          <SigLine label="Operator signature" ok={result?.operator} />
        </div>
        <div>
          <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Public key used (governor)</p>
          <p className="mono text-[10px] text-cyan-300 break-all leading-relaxed">{entry.governor_pubkey}</p>
        </div>
        <div>
          <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Signed payload (canonical)</p>
          <p className="mono text-[10px] text-slate-300 break-all leading-relaxed">{entry.canonical_payload}</p>
        </div>
        {entry.operator_payload && (
          <div>
            <p className="mono text-[9px] uppercase tracking-widest text-slate-500 mb-1">Operator payload (SHA-256 signed)</p>
            <p className="mono text-[10px] text-slate-300 break-all leading-relaxed">{entry.operator_payload}</p>
          </div>
        )}
      </div>
    </td>
  </tr>
);

const fmt = (iso) => {
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return iso;
  }
};

export const AuditLog = ({ entries, onUpdateEntries }) => {
  const [results, setResults] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [tampering, setTampering] = useState(false);
  // Show most recent first.
  const rows = [...entries].reverse();
  const tamperedSeqs = entries.filter((e) => e.status !== "Canonical").map((e) => e.batch_seq);
  const brokenFrom = tamperedSeqs.length ? Math.min(...tamperedSeqs) : null;
  const affectedCount = brokenFrom !== null ? entries.filter((e) => e.batch_seq >= brokenFrom).length : 0;

  // Verify every row's signatures in-browser (real time) whenever entries change.
  useEffect(() => {
    const next = {};
    entries.forEach((e) => {
      next[e.id] = verifyEntry(e);
    });
    setResults(next);
  }, [entries]);

  const tamperLatest = async () => {
    if (!rows.length) return;
    const target = rows[0];
    setTampering(true);
    try {
      const updated = await tamperAudit(target.id);
      onUpdateEntries?.((prev) =>
        prev.map((x) => (x.id === updated.id ? updated : x))
      );
      setExpanded(updated.id);
      toast.warning("Entry #" + String(updated.batch_seq).padStart(3, "0") + " altered", {
        description: "Record bytes changed after sealing — signature now fails verification.",
      });
    } catch {
      toast.error("Tamper simulation failed");
    } finally {
      setTampering(false);
    }
  };

  const exportJson = async () => {
    setExporting(true);
    try {
      const data = await exportLedgerJson();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "audit_ledger_signed.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Signed JSON ledger exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="glass-panel rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Database className="h-4 w-4 text-cyan-400" />
          <h3 className="mono text-xs font-semibold uppercase tracking-widest text-cyan-400">
            Sealed Audit Ledger
          </h3>
          <span className="mono text-[10px] text-slate-500 border border-slate-800 rounded px-2 py-0.5">
            {entries.length} entries
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="hidden lg:flex items-center gap-1.5 mono text-[10px] text-slate-500 mr-1">
            <KeyRound className="h-3 w-3 text-teal-400" />
            Ed25519 · governor + auditor + operator
          </span>
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="verify-guide-button"
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900/60 px-2.5 py-1.5 mono text-[10px] text-slate-300 hover:border-cyan-600/60 hover:text-cyan-300 transition-colors"
              >
                <ShieldCheck className="h-3 w-3" /> How to verify
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[380px] bg-[#0b1120] border-slate-800 text-slate-200"
              data-testid="verify-guide-popover"
            >
              <p className="mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2">
                Verify a signature offline
              </p>
              <p className="text-[11px] text-slate-400 leading-relaxed mb-3">
                Every exported record includes the <span className="text-slate-200">canonical</span> string,
                the Ed25519 <span className="text-slate-200">signature</span>, and the{" "}
                <span className="text-slate-200">governor_pubkey</span>. Re-check them locally with any
                Ed25519 library — no network required:
              </p>
              <pre className="rounded-lg bg-slate-950 border border-slate-800 p-3 mono text-[10px] text-cyan-200 overflow-x-auto whitespace-pre leading-relaxed">
{VERIFY_CODE}
              </pre>
              <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                If <span className="text-emerald-300 mono">verify()</span> raises no error, the record is
                authentic and unmodified. Altering any canonical byte makes verification fail — that is what
                the in-app Verify button and the tamper demo exercise.
              </p>
            </PopoverContent>
          </Popover>
          <button
            data-testid="tamper-button"
            onClick={tamperLatest}
            disabled={tampering || entries.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-amber-800/60 bg-amber-950/20 px-2.5 py-1.5 mono text-[10px] text-amber-300/90 hover:border-amber-600/70 hover:text-amber-200 disabled:opacity-40 transition-colors"
            title="Demo: alter the most recent entry to break its signature"
          >
            {tampering ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldAlert className="h-3 w-3" />}
            Simulate Tamper
          </button>
          <button
            data-testid="export-json-button"
            onClick={exportJson}
            disabled={exporting || entries.length === 0}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900/60 px-2.5 py-1.5 mono text-[10px] text-slate-300 hover:border-cyan-600/60 hover:text-cyan-300 disabled:opacity-40 transition-colors"
          >
            <Download className="h-3 w-3" /> Signed JSON
          </button>
          <a
            data-testid="export-csv-button"
            href={entries.length ? csvExportUrl() : undefined}
            onClick={(e) => entries.length === 0 && e.preventDefault()}
            className={`inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900/60 px-2.5 py-1.5 mono text-[10px] transition-colors ${
              entries.length
                ? "text-slate-300 hover:border-cyan-600/60 hover:text-cyan-300"
                : "text-slate-600 opacity-40 pointer-events-none"
            }`}
          >
            <Download className="h-3 w-3" /> CSV
          </a>
        </div>
      </div>

      {brokenFrom !== null && (
        <div data-testid="chain-break-banner" className="mb-3 flex items-center gap-2 rounded-lg border border-amber-700/60 bg-amber-950/30 px-3 py-2">
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
          <p className="mono text-[11px] text-amber-200/90">
            Chain integrity broken from entry #{String(brokenFrom).padStart(3, "0")} — {affectedCount}{" "}
            {affectedCount === 1 ? "entry descends" : "entries descend"} from an altered link and can no longer be trusted.
          </p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <div className="max-h-[340px] overflow-y-auto">
          <table data-testid="audit-log-table" className="w-full text-left border-collapse">
            <thead className="sticky top-0 z-10 bg-slate-950/95 backdrop-blur">
              <tr className="mono text-[10px] uppercase tracking-widest text-slate-500">
                <Th>Seq</Th>
                <Th>Timestamp</Th>
                <Th>Domain</Th>
                <Th>Fault Type</Th>
                <Th>Correction Note</Th>
                <Th>Merkle Root (batch)</Th>
                <Th>Governor Sig</Th>
                <Th>Auditor Sig</Th>
                <Th>Operator Sig</Th>
                <Th>Status</Th>
                <Th>Verify</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center mono text-xs text-slate-600">
                    No committed entries yet. Approve a correction to seal the first audit record.
                  </td>
                </tr>
              ) : (
                rows.map((e, i) => {
                  const isTampered = e.status !== "Canonical";
                  const downstream = brokenFrom !== null && e.batch_seq > brokenFrom && !isTampered;
                  return (
                  <Fragment key={e.id}>
                  <tr
                    data-testid="audit-log-row"
                    className={`border-t border-slate-800/70 hover:bg-slate-800/30 transition-colors ${
                      i === 0 ? "row-in" : ""
                    } ${
                      isTampered
                        ? "bg-red-950/20 border-l-2 border-l-red-600"
                        : downstream
                        ? "bg-amber-950/10 border-l-2 border-l-amber-600/70"
                        : ""
                    }`}
                  >
                    <Td className="mono text-[11px] text-cyan-300 whitespace-nowrap">#{String(e.batch_seq).padStart(3, "0")}</Td>
                    <Td className="mono text-[11px] text-slate-400 whitespace-nowrap">{fmt(e.timestamp)}</Td>
                    <Td className="text-xs text-slate-200 whitespace-nowrap">{e.domain}</Td>
                    <Td className="mono text-[11px] text-amber-300 whitespace-nowrap">{e.fault_type}</Td>
                    <Td className="text-xs text-slate-300 max-w-[220px] truncate" title={e.correction_note}>
                      {e.correction_note}
                    </Td>
                    <Td><MerkleCell entry={e} broken={isTampered ? "tampered" : downstream ? "downstream" : null} brokenFrom={brokenFrom} /></Td>
                    <Td><Sig value={e.governor_signature} /></Td>
                    <Td><Sig value={e.auditor_signature} /></Td>
                    <Td><Sig value={e.operator_signature} /></Td>
                    <Td>
                      {e.status === "Canonical" ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-600/50 bg-emerald-950/50 px-2 py-0.5 mono text-[10px] font-semibold text-emerald-300 whitespace-nowrap">
                          <BadgeCheck className="h-3 w-3" />
                          {e.status}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full border border-red-600/50 bg-red-950/50 px-2 py-0.5 mono text-[10px] font-semibold text-red-300 whitespace-nowrap">
                          <AlertTriangle className="h-3 w-3" />
                          {e.status}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <VerifyButton
                        result={results[e.id]}
                        expanded={expanded === e.id}
                        onClick={() => setExpanded(expanded === e.id ? null : e.id)}
                      />
                    </Td>
                  </tr>
                  {expanded === e.id && <VerifyDrawer entry={e} result={results[e.id]} />}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Th = ({ children }) => <th className="px-3 py-2.5 font-medium">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 ${className}`}>{children}</td>;
